import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, MessageCircle, Instagram, Send, Globe, ExternalLink, Layers } from 'lucide-react';
import { ProviderVersionsDialog } from '@/components/admin/ProviderVersionsDialog';

// Tipo para canal vindo da tabela channel_types
type ChannelType = string;
type AuthType = 'api_key' | 'oauth2' | 'basic' | 'webhook_secret' | 'custom' | 'token';

// Interface para tipos de canal carregados do banco
interface ChannelTypeOption {
  slug: string;
  display_name: string;
}

interface ApiProvider {
  id: string;
  name: string;
  display_name: string | null; // Apelido do provider exibido ao tenant
  slug: string;
  channel_type: ChannelType;
  auth_type: AuthType;
  base_url: string | null;
  documentation_url: string | null;
  description: string | null;
  logo_url: string | null;
  is_active: boolean;
  created_at: string;
  version_count?: number;
}

// Configuração visual para ícones e cores dos canais (fallback)
const channelVisualConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  whatsapp: { icon: MessageCircle, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  whatsapp_oficial: { icon: MessageCircle, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  messenger: { icon: MessageCircle, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  instagram: { icon: Instagram, color: 'bg-pink-500/10 text-pink-600 border-pink-500/20' },
  webchat: { icon: Globe, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  telegram: { icon: Send, color: 'bg-sky-500/10 text-sky-600 border-sky-500/20' },
};

// Função auxiliar para obter visual do canal
const getChannelVisual = (slug: string, displayName: string) => {
  const visual = channelVisualConfig[slug] || { icon: MessageCircle, color: 'bg-gray-500/10 text-gray-600 border-gray-500/20' };
  return { ...visual, label: displayName };
};

const authTypeLabels: Record<AuthType, string> = {
  api_key: 'API Key',
  oauth2: 'OAuth 2.0',
  basic: 'Basic Auth',
  webhook_secret: 'Webhook Secret',
  custom: 'Custom',
  token: 'Token Manual',
};

const emptyProvider = {
  name: '',
  display_name: '', // Apelido para exibir ao tenant
  slug: '',
  channel_type: 'whatsapp' as ChannelType,
  auth_type: 'api_key' as AuthType,
  base_url: '',
  documentation_url: '',
  description: '',
  is_active: true,
};

export default function Providers() {
  const [providers, setProviders] = useState<ApiProvider[]>([]);
  const [channelTypes, setChannelTypes] = useState<ChannelTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ApiProvider | null>(null);
  const [formData, setFormData] = useState(emptyProvider);
  const [saving, setSaving] = useState(false);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [selectedProviderForVersions, setSelectedProviderForVersions] = useState<ApiProvider | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProviders();
    fetchChannelTypes();
  }, []);

  // Busca tipos de canal da tabela channel_types
  const fetchChannelTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('channel_types')
        .select('slug, display_name')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setChannelTypes(data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de canal:', error);
    }
  };

  // Obtém display_name do canal pelo slug
  const getChannelDisplayName = (slug: string) => {
    const found = channelTypes.find(ct => ct.slug === slug);
    return found?.display_name || slug;
  };

  const fetchProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('api_providers')
        .select('*')
        .order('channel_type', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setProviders(data || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast({
        title: 'Erro ao carregar fornecedores',
        description: 'Não foi possível carregar a lista de fornecedores.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingProvider(null);
    setFormData(emptyProvider);
    setDialogOpen(true);
  };

  const handleOpenEdit = (provider: ApiProvider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      display_name: provider.display_name || '',
      slug: provider.slug,
      channel_type: provider.channel_type,
      auth_type: provider.auth_type,
      base_url: provider.base_url || '',
      documentation_url: provider.documentation_url || '',
      description: provider.description || '',
      is_active: provider.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Nome e slug são obrigatórios.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Cast para o tipo esperado pelo Supabase (enum channel_type)
      type DbChannelType = 'whatsapp' | 'whatsapp_oficial' | 'messenger' | 'instagram' | 'webchat' | 'telegram';
      
      const payload = {
        name: formData.name,
        display_name: formData.display_name || null,
        slug: formData.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        channel_type: formData.channel_type as DbChannelType,
        auth_type: formData.auth_type,
        base_url: formData.base_url || null,
        documentation_url: formData.documentation_url || null,
        description: formData.description || null,
        is_active: formData.is_active,
      };

      if (editingProvider) {
        const { error } = await supabase
          .from('api_providers')
          .update(payload)
          .eq('id', editingProvider.id);

        if (error) throw error;
        toast({ title: 'Fornecedor atualizado com sucesso!' });
      } else {
        const { error } = await supabase
          .from('api_providers')
          .insert(payload);

        if (error) throw error;
        toast({ title: 'Fornecedor criado com sucesso!' });
      }

      setDialogOpen(false);
      fetchProviders();
    } catch (error: any) {
      console.error('Error saving provider:', error);
      toast({
        title: 'Erro ao salvar fornecedor',
        description: error.message || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (provider: ApiProvider) => {
    try {
      const { error } = await supabase
        .from('api_providers')
        .update({ is_active: !provider.is_active })
        .eq('id', provider.id);

      if (error) throw error;
      toast({
        title: provider.is_active ? 'Fornecedor desativado' : 'Fornecedor ativado',
      });
      fetchProviders();
    } catch (error) {
      console.error('Error toggling provider:', error);
      toast({
        title: 'Erro ao atualizar status',
        variant: 'destructive',
      });
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Fornecedores de API</h1>
          <p className="text-muted-foreground">
            Gerencie os provedores de integração disponíveis para os canais de comunicação.
          </p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Fornecedor
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Canal</TableHead>
              <TableHead>Autenticação</TableHead>
              <TableHead>Versões</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : providers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhum fornecedor cadastrado.
                </TableCell>
              </TableRow>
            ) : (
              providers.map((provider) => {
                const displayName = getChannelDisplayName(provider.channel_type);
                const channel = getChannelVisual(provider.channel_type, displayName);
                const ChannelIcon = channel.icon;
                return (
                  <TableRow key={provider.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{provider.name}</div>
                        <div className="text-sm text-muted-foreground">{provider.slug}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={channel.color}>
                        <ChannelIcon className="h-3 w-3 mr-1" />
                        {channel.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{authTypeLabels[provider.auth_type]}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProviderForVersions(provider);
                          setVersionsDialogOpen(true);
                        }}
                      >
                        <Layers className="h-4 w-4 mr-1" />
                        Gerenciar
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={provider.is_active}
                        onCheckedChange={() => handleToggleActive(provider)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(provider)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {provider.documentation_url && (
                          <Button variant="ghost" size="icon" asChild>
                            <a href={provider.documentation_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </DialogTitle>
            <DialogDescription>
              {editingProvider
                ? 'Atualize as informações do fornecedor de API.'
                : 'Cadastre um novo fornecedor de integração.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0 pr-2">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome Interno *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: editingProvider ? formData.slug : generateSlug(e.target.value),
                  });
                }}
                placeholder="Ex: Z-API"
              />
              <p className="text-xs text-muted-foreground">Nome real do provider (não exibido ao tenant)</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="display_name">Apelido (Display Name)</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="Ex: WhatsApp Connector"
              />
              <p className="text-xs text-muted-foreground">Nome exibido ao tenant na criação de inbox</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="Ex: z-api"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Canal *</Label>
                <Select
                  value={formData.channel_type}
                  onValueChange={(value: ChannelType) => setFormData({ ...formData, channel_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {channelTypes.map((ct) => (
                      <SelectItem key={ct.slug} value={ct.slug}>
                        {ct.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Autenticação *</Label>
                <Select
                  value={formData.auth_type}
                  onValueChange={(value: AuthType) => setFormData({ ...formData, auth_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(authTypeLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="base_url">URL Base</Label>
              <Input
                id="base_url"
                value={formData.base_url}
                onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                placeholder="https://api.exemplo.com"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="documentation_url">URL Documentação</Label>
              <Input
                id="documentation_url"
                value={formData.documentation_url}
                onChange={(e) => setFormData({ ...formData, documentation_url: e.target.value })}
                placeholder="https://docs.exemplo.com"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrição do fornecedor..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Ativo</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : editingProvider ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      {selectedProviderForVersions && (
        <ProviderVersionsDialog
          open={versionsDialogOpen}
          onOpenChange={setVersionsDialogOpen}
          providerId={selectedProviderForVersions.id}
          providerName={selectedProviderForVersions.name}
        />
      )}
    </div>
  );
}
